import { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** `00:00`, `00:30`, … `23:30` — la grilla que se ofrece. */
function grillaMediaHora(): string[] {
  return Array.from({ length: 48 }, (_, i) => {
    const hora = String(Math.floor(i / 2)).padStart(2, "0");
    return `${hora}:${i % 2 === 0 ? "00" : "30"}`;
  });
}

export interface CampoHoraProps {
  id?: string;
  /** `HH:MM` en 24 h. */
  value: string;
  onChange: (hhmm: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Campo de hora del Design System — reemplaza a `<input type="time">`.
 *
 * Mismo motivo que [[CampoFecha]]: el input nativo dibuja el valor según el
 * idioma del navegador y las 14:30 se leen `02:30 p.m.` en inglés, con
 * variantes por motor (`09 : 00 a. m.` en Firefox). Acá el 24 h es fijo.
 *
 * Es un `Select` de media en media hora y no un campo libre a propósito: la
 * hora define a qué hora sale un correo masivo, donde un 14:37 no aporta nada
 * sobre las 14:30 y sí abre la puerta a tipeos. Si el valor guardado cae fuera
 * de la grilla —lo pudo dejar el input libre anterior— se agrega como opción
 * para no perderlo ni mostrar el campo vacío.
 */
export function CampoHora({ id, value, onChange, disabled = false, className }: CampoHoraProps) {
  const opciones = useMemo(() => {
    const grilla = grillaMediaHora();
    return value && !grilla.includes(value)
      ? [...grilla, value].sort()
      : grilla;
  }, [value]);

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} className={className ?? "w-full"}>
        <SelectValue placeholder="--:--" />
      </SelectTrigger>
      <SelectContent>
        {opciones.map((hhmm) => (
          <SelectItem key={hhmm} value={hhmm}>
            {hhmm}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
